import React, { useState, useLayoutEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  setClickedFeature,
  setHoveredFeature,
  setSidebarIsOpen,
} from "../../actions/ui";
import {
  selectClickedFeature,
  selectHoveredFeature,
  selectZoomToNsw,
  selectGeoJSON,
  selectSidebarIsOpen,
  selectAllRegionContent,
} from "../../reducers/";
import styles from "./map.module.scss";
import "./map.css";
import DeckGL from "@deck.gl/react";
import { FlyToInterpolator } from "@deck.gl/core";

import { GeoJsonLayer } from "@deck.gl/layers";
import ReactMapGl, { NavigationControl, Popup } from "react-map-gl";
import WebMercatorViewport from "viewport-mercator-project";
import "mapbox-gl/dist/mapbox-gl.css";
import "./map.scss";
import { feature as topoToGeo } from "topojson-client";
import Bowser from "bowser";
import ReactGA from "react-ga";
import { isMobile } from "react-device-detect";
const bowser = Bowser.getParser(window.navigator.userAgent);
const browser = bowser.getBrowser();
const isIE11 =
  browser.version === "11.0" && browser.name === "Internet Explorer";

const NSW_BBOX = [
  135.49927903600008, -37.90507657099996, 153.43883173500005,
  -27.457020554999974,
];

const TRANSPARENT_WHITE = [255, 255, 255, 255 * 0.2];
const TRANSPARENT = [255, 255, 255, 1];

const Map = (props) => {
  const initialViewport = {
    width: props.width,
    height: props.height,
  };
  const {
    clickedFeature,
    hoveredFeature,
    isSidebarOpen,
    setClickedFeature,
    setSidebarIsOpen,
    setHoveredFeature,
    geometryConfig,
    allRegionContent,
    zoomToNsw,
  } = props;

  const mapRef = useRef();

  const createNewViewport = React.useCallback(
    (viewState) => {
      let bboxActive = [NSW_BBOX.slice(0, 2), NSW_BBOX.slice(2, 4)];

      const newViewport = new WebMercatorViewport({
        ...initialViewport,
        ...viewState,
      }).fitBounds(bboxActive);

      return newViewport;
    },
    [isSidebarOpen]
  );

  const initialViewState = React.useMemo(() => {
    const initialViewState = createNewViewport({});

    return initialViewState;
  }, []);
  const [viewState, setViewState] = useState(initialViewState);

  const { idSelector } = geometryConfig;

  const reset = React.useCallback(() => {
    setViewState((x) => {
      const newViewport = createNewViewport(x);
      return {
        ...x,
        latitude: newViewport.latitude,
        longitude: newViewport.longitude,
        zoom: newViewport.zoom,
        transitionInterpolator: new FlyToInterpolator(),
        transitionDuration: 200,
      };
    });
  });

  const hoveredRegionContent =
    allRegionContent && clickedFeature
      ? allRegionContent[clickedFeature.properties["Functional"]]
      : null;

  useLayoutEffect(() => {
    if (!mapRef.current || !mapRef.current.getMap()) {
      return;
    }

    setViewState((viewState) => {
      const newViewState = createNewViewport(viewState);
      return {
        latitude: newViewState.latitude,
        longitude: newViewState.longitude,
        zoom: newViewState.zoom,
        transitionInterpolator: new FlyToInterpolator(),
        transitionDuration: 200,
      };
    });
  }, [zoomToNsw]);

  return (
    <ReactMapGl
      style={{ position: "absolute", top: 0, left: 0 }}
      width={initialViewport.width}
      height={initialViewport.height}
      initialViewState={initialViewState}
      viewState={viewState}
      onViewportChange={(x) => {
        setViewState((currentViewState) => ({
          ...currentViewState,
          ...x,
        }));
      }}
      ref={mapRef}
      mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_API_TOKEN}
      mapStyle={"mapbox://styles/smallmultiples/cl7yrnyrd000114r1y7x2ee70"}
      getCursor={({ isDragging }) => {
        if (clickedFeature) {
          return "pointer";
        }
        return isDragging ? "grabbing" : "grab";
      }}
    >
      <DeckGL
        width={initialViewport.width}
        height={initialViewport.height}
        viewState={viewState}
        controller={false}
        getCursor={() => "pointer"}
        layers={[
          new GeoJsonLayer({
            id: "electorates", // these are lgas actually
            data: geometryConfig.geojsonUrl,
            dataTransform: (data) => {
              const geojson = topoToGeo(
                data,
                data.objects[geometryConfig.topologyObjectsName]
              );
              return geojson;
            },
            stroked: true,
            lineWidthUnits: "pixels",
            // Increase line width if feature is hovered or active
            getLineWidth: (d) => {
              if (
                clickedFeature &&
                d.properties["Functional"] ===
                  clickedFeature.properties["Functional"]
              ) {
                return 2;
              } else {
                return 0;
              }
            },
            lineWidthMinPixels: 0,
            getFillColor: (d) => {
              if (
                clickedFeature &&
                d.properties["Functional"] ===
                  clickedFeature.properties["Functional"]
              ) {
                return TRANSPARENT;
              } else {
                return TRANSPARENT_WHITE;
              }
            },
            highlightColor: TRANSPARENT,
            getLineColor: [14, 183, 108, 255],
            pickable: true,
            updateTriggers: {
              // Make sure the map updates when a feature is hovered/active
              // So we can redraw line widths and change transparency
              // But not on IE11 as it doesn't work
              getFillColor: isIE11 ? [] : [clickedFeature],
              getLineWidth: [clickedFeature],
            },
            autoHighlight: true,
            onClick: (d) => {
              setClickedFeature(d.object);
              setTimeout(function () {
                setSidebarIsOpen(true);
              }, 1000);

              const regionContent = allRegionContent
                ? allRegionContent[d.object.properties["Functional"]]
                : null;

              ReactGA.event({
                category: "REDS map",
                action: "Click map for more detail",
                label: d.object.properties["Functional"],
              });
            },
          }),
        ]}
        getTooltip={({ object }) => {
          if (object) {
            setHoveredFeature(object);
          }
        }}
      />

      {hoveredFeature && (
        <Popup
          longitude={
            (hoveredFeature.properties.bbox[0] +
              hoveredFeature.properties.bbox[2]) /
            2
          }
          latitude={hoveredFeature.properties.bbox[3]}
          anchor="bottom"
          onClose={() => setHoveredFeature(null)}
        >
          <p className={styles.popUpPar}>
            Click for more detail about <br />
            <strong>{hoveredFeature.properties["Functional"]}</strong>{" "}
          </p>
        </Popup>
      )}

      <div className={styles.navigationControlBtns}>
        <NavigationControl
          onViewportChange={(newViewState) => {
            setViewState((x) => ({
              ...x,
              zoom: newViewState.zoom,
              latitude: newViewState.latitude,
              longitude: newViewState.longitude,
              transitionInterpolator: new FlyToInterpolator(),
              transitionDuration: 200,
            }));
          }}
          showCompass={false}
          style={{ width: "40px" }}
        />
      </div>
    </ReactMapGl>
  );
};

const featureShape = {
  type: PropTypes.string.isRequired,
  geometry: PropTypes.object.isRequired,
  properties: PropTypes.object.isRequired,
};
Map.propTypes = {
  clickedFeature: PropTypes.shape(featureShape),
  setClickedFeature: PropTypes.func.isRequired,
  hoveredFeature: PropTypes.shape(featureShape),
  setHoveredFeature: PropTypes.func.isRequired,
  setSidebarIsOpen: PropTypes.func.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  geometryConfig: PropTypes.object.isRequired,
  zoomToNsw: PropTypes.number.isRequired,
  geojson: PropTypes.object,
  isSidebarOpen: PropTypes.bool.isRequired,
  allRegionContent: PropTypes.object,
};

const mapStateToProps = (state) => ({
  clickedFeature: selectClickedFeature(state),
  hoveredFeature: selectHoveredFeature(state),
  zoomToNsw: selectZoomToNsw(state),
  geojson: selectGeoJSON(state),
  isSidebarOpen: selectSidebarIsOpen(state),
  allRegionContent: selectAllRegionContent(state),
});
const mapDispatchToProps = {
  setClickedFeature,
  setHoveredFeature,
  setSidebarIsOpen,
};

export default connect(mapStateToProps, mapDispatchToProps)(Map);



// WEBPACK FOOTER //
// ./src/components/map/map.js